/**
 * Filterable tutorial title directory used by the tutorial blog template.
 * The element is intentionally self-contained so it reconnects automatically
 * when blog-tags.js replaces #Blog-Content after an AJAX tag change.
 */
if (!customElements.get("blog-directory")) {
  customElements.define(
    "blog-directory",
    class BlogDirectory extends HTMLElement {
      connectedCallback() {
        if (this.initialized) {
          this.tooltipTriggers = Array.from(
            this.querySelectorAll("[data-tutorial-directory-tooltip]"),
          );
          this.initTooltips();
          return;
        }

        this.input = this.querySelector("[data-tutorial-directory-input]");
        this.clearButton = this.querySelector(
          "[data-tutorial-directory-clear]",
        );
        this.count = this.querySelector("[data-tutorial-directory-count]");
        this.totalCount = this.querySelector("[data-tutorial-directory-total]");
        this.totalBadge = this.querySelector(".tutorial-directory__total");
        this.empty = this.querySelector("[data-tutorial-directory-empty]");
        this.maintenance = this.querySelector(
          "[data-tutorial-directory-maintenance-status]",
        );
        this.unlinkedDetails = this.querySelector(
          "[data-tutorial-directory-unlinked-details]",
        );
        this.unlinkedCount = this.querySelector(
          "[data-tutorial-directory-unlinked-count]",
        );
        this.unlinkedLabel = this.querySelector(
          "[data-tutorial-directory-unlinked-label]",
        );
        this.unlinkedList = this.querySelector(
          "[data-tutorial-directory-unlinked-list]",
        );
        this.duplicateDetails = this.querySelector(
          "[data-tutorial-directory-duplicate-details]",
        );
        this.duplicateCount = this.querySelector(
          "[data-tutorial-directory-duplicate-count]",
        );
        this.duplicateList = this.querySelector(
          "[data-tutorial-directory-duplicate-list]",
        );
        this.details = this.querySelector(".tutorial-directory__details");
        this.tooltipTriggers = Array.from(
          this.querySelectorAll("[data-tutorial-directory-tooltip]"),
        );

        this.initTooltips();

        if (!this.input) return;
        this.initialized = true;

        this.mobileQuery = window.matchMedia("(max-width: 989px)");
        this.syncDisclosure(this.mobileQuery);
        if (this.mobileQuery.addEventListener) {
          this.mobileQuery.addEventListener("change", (event) =>
            this.syncDisclosure(event),
          );
        } else {
          this.mobileQuery.addListener((event) => this.syncDisclosure(event));
        }

        this.refreshItems();

        this.input.addEventListener("input", () => this.filter());
        this.input.addEventListener("search", () => this.filter());
        this.input.addEventListener("keydown", (event) => {
          if (event.key === "Escape" && this.input.value) {
            event.preventDefault();
            this.clear();
          }
        });

        if (this.clearButton) {
          this.clearButton.addEventListener("click", () => this.clear());
        }

        this.loadCompleteDirectory();
      }

      disconnectedCallback() {
        this.destroyTooltips();
      }

      initTooltips() {
        if (this.tooltip || !this.tooltipTriggers.length) return;

        this.tooltip = document.createElement("div");
        this.tooltip.className = "tutorial-directory__tooltip";
        this.tooltip.setAttribute("role", "tooltip");
        this.tooltip.hidden = true;
        document.body.appendChild(this.tooltip);

        this.tooltipListeners = [];
        this.tooltipTriggers.forEach((trigger) => {
          const onPointerEnter = (event) => {
            if (event.pointerType !== "touch") {
              this.showTooltip(trigger, "pointer");
            }
          };
          const onPointerLeave = (event) => {
            if (event.pointerType !== "touch") this.hideTooltip();
          };
          const onPointerDown = (event) => {
            if (event.pointerType === "touch") {
              this.showTooltip(trigger, "touch");
              window.clearTimeout(this.tooltipTimeout);
              this.tooltipTimeout = window.setTimeout(
                () => this.hideTooltip(),
                4000,
              );
            }
          };
          const onFocus = () => {
            if (trigger.matches(":focus-visible")) {
              this.showTooltip(trigger, "keyboard");
            }
          };
          const onBlur = () => this.hideTooltip();
          const onKeyDown = (event) => {
            if (event.key === "Escape") this.hideTooltip();
          };

          trigger.addEventListener("pointerenter", onPointerEnter);
          trigger.addEventListener("pointerleave", onPointerLeave);
          trigger.addEventListener("pointerdown", onPointerDown);
          trigger.addEventListener("focus", onFocus);
          trigger.addEventListener("blur", onBlur);
          trigger.addEventListener("keydown", onKeyDown);
          this.tooltipListeners.push({
            trigger,
            onPointerEnter,
            onPointerLeave,
            onPointerDown,
            onFocus,
            onBlur,
            onKeyDown,
          });
        });

        this.onTooltipViewportChange = () => {
          if (!this.activeTooltipTrigger) return;
          if (this.tooltipInputType === "touch") {
            this.hideTooltip();
          } else {
            this.positionTooltip(this.activeTooltipTrigger);
          }
        };
        this.onTooltipDocumentPointerDown = (event) => {
          if (
            this.tooltipInputType === "touch" &&
            !event.target.closest("[data-tutorial-directory-tooltip]")
          ) {
            this.hideTooltip();
          }
        };
        window.addEventListener("resize", this.onTooltipViewportChange);
        document.addEventListener("scroll", this.onTooltipViewportChange, true);
        document.addEventListener(
          "pointerdown",
          this.onTooltipDocumentPointerDown,
          true,
        );
      }

      showTooltip(trigger, inputType) {
        if (!this.tooltip || !trigger.dataset.tutorialDirectoryTooltip) return;

        window.clearTimeout(this.tooltipTimeout);
        this.activeTooltipTrigger = trigger;
        this.tooltipInputType = inputType;
        this.tooltip.textContent = trigger.dataset.tutorialDirectoryTooltip;
        this.tooltip.hidden = false;
        this.positionTooltip(trigger);
      }

      positionTooltip(trigger) {
        if (!this.tooltip || this.tooltip.hidden || !trigger.isConnected) return;

        const gap = 8;
        const viewportPadding = 12;
        const triggerRect = trigger.getBoundingClientRect();
        const tooltipRect = this.tooltip.getBoundingClientRect();
        let left = triggerRect.right + gap;
        let top =
          triggerRect.top + (triggerRect.height - tooltipRect.height) / 2;

        if (left + tooltipRect.width > window.innerWidth - viewportPadding) {
          left = triggerRect.left - tooltipRect.width - gap;
        }
        if (left < viewportPadding) {
          left = Math.min(
            Math.max(triggerRect.left, viewportPadding),
            window.innerWidth - tooltipRect.width - viewportPadding,
          );
          top = triggerRect.bottom + gap;
          if (top + tooltipRect.height > window.innerHeight - viewportPadding) {
            top = triggerRect.top - tooltipRect.height - gap;
          }
        }

        left = Math.max(
          viewportPadding,
          Math.min(
            left,
            window.innerWidth - tooltipRect.width - viewportPadding,
          ),
        );
        top = Math.max(
          viewportPadding,
          Math.min(
            top,
            window.innerHeight - tooltipRect.height - viewportPadding,
          ),
        );
        this.tooltip.style.left = `${Math.round(left)}px`;
        this.tooltip.style.top = `${Math.round(top)}px`;
      }

      hideTooltip() {
        window.clearTimeout(this.tooltipTimeout);
        if (this.tooltip) this.tooltip.hidden = true;
        this.activeTooltipTrigger = null;
        this.tooltipInputType = null;
      }

      destroyTooltips() {
        window.clearTimeout(this.tooltipTimeout);
        (this.tooltipListeners || []).forEach((listener) => {
          listener.trigger.removeEventListener(
            "pointerenter",
            listener.onPointerEnter,
          );
          listener.trigger.removeEventListener(
            "pointerleave",
            listener.onPointerLeave,
          );
          listener.trigger.removeEventListener(
            "pointerdown",
            listener.onPointerDown,
          );
          listener.trigger.removeEventListener("focus", listener.onFocus);
          listener.trigger.removeEventListener("blur", listener.onBlur);
          listener.trigger.removeEventListener("keydown", listener.onKeyDown);
        });
        window.removeEventListener("resize", this.onTooltipViewportChange);
        document.removeEventListener(
          "scroll",
          this.onTooltipViewportChange,
          true,
        );
        document.removeEventListener(
          "pointerdown",
          this.onTooltipDocumentPointerDown,
          true,
        );
        this.tooltip?.remove();
        this.tooltip = null;
      }

      refreshItems() {
        this.groups = Array.from(
          this.querySelectorAll("[data-tutorial-directory-group]"),
        );
        this.items = Array.from(
          this.querySelectorAll("[data-tutorial-directory-item]"),
        );
        this.total = this.items.length;

        this.items.forEach((item, index) => {
          const title =
            item.dataset.title ||
            item.querySelector("a")?.textContent ||
            item.textContent ||
            "";
          item._normalizedTitle = this.normalize(title);
          const number = item.querySelector(".tutorial-directory__number");
          if (number) number.textContent = String(index + 1).padStart(2, "0");
        });

        this.groups.forEach((group) => {
          group._directoryItems = Array.from(
            group.querySelectorAll("[data-tutorial-directory-item]"),
          );
          const groupCount = group.querySelector(
            ":scope > .tutorial-directory__group-summary [data-tutorial-directory-group-count]",
          );
          if (groupCount) {
            groupCount.dataset.total = String(group._directoryItems.length);
            groupCount.textContent = String(group._directoryItems.length);
          }
        });

        if (this.totalCount) this.totalCount.textContent = String(this.total);
        if (this.totalBadge) {
          this.totalBadge.textContent = String(this.total);
          this.totalBadge.setAttribute("aria-label", `${this.total} tutorials`);
        }
        if (this.count) this.count.textContent = String(this.total);
        if (this.input) this.input.disabled = this.total === 0;
        if (this.empty && !this.total) {
          this.empty.textContent = "No directory items have been added yet.";
          this.empty.classList.remove("hidden");
        }
      }

      async loadCompleteDirectory() {
        const source = this.dataset.directorySource || "automatic";
        const includeUnlinked = this.dataset.includeUnlinked === "true";
        const isDesignMode = this.dataset.designMode === "true";

        // 自动模式总是拉取全部文章；手工模式仅在需要补充未链接文章时拉取。
        // 但后台主题编辑器（维护面板）始终需要文章索引来计算诊断统计。
        const shouldLoad =
          source === "automatic" || includeUnlinked || isDesignMode;
        if (!shouldLoad || !this.dataset.blogUrl) return;

        try {
          const articles = await this.fetchAllArticles();
          const diagnostics = this.buildDiagnostics(articles, source);

          if (source === "manual") {
            if (includeUnlinked) this.appendUnlinkedArticles(articles);
          } else {
            this.appendMissingAutomaticArticles(articles);
          }

          this.renderMaintenance(diagnostics);
          this.refreshItems();
          this.filter();
        } catch (error) {
          if (this.maintenance) {
            this.maintenance.textContent =
              "检查失败，现有目录未受影响；请刷新页面后重试。";
          }
          console.warn("[blog-directory] failed to load the article index", error);
        }
      }

      // 统一在两种模式下计算维护统计，保证后台维护人员看到一致口径。
      buildDiagnostics(articles, source) {
        const configuredEntries = this.getLinkedEntries();
        const configuredUrls = configuredEntries.map((entry) => entry.url);
        const linked = new Set(configuredUrls);
        const duplicateLinkCount = configuredUrls.length - linked.size;

        let linkedArticleCount = 0;
        let hiddenCount = 0;
        let uncategorizedCount = 0;
        const additions = [];

        articles.forEach((article) => {
          const hidden = this.isArticleHidden(article);
          if (hidden) hiddenCount += 1;
          const category = this.getArticleCategory(article);

          if (source === "automatic") {
            if (!hidden && !category) {
              uncategorizedCount += 1;
              additions.push({ article, category: "", subcategory: "" });
            }
            return;
          }

          const url = this.normalizeUrl(article.url);
          if (url && linked.has(url)) {
            linkedArticleCount += 1;
            return;
          }
          if (hidden) return;

          const subcategory = category ? this.getArticleSubcategory(article) : "";
          if (!category) uncategorizedCount += 1;
          additions.push({ article, category, subcategory });
        });

        return {
          totalArticles: articles.length,
          configuredEntries,
          configuredItemCount: this.items.length,
          linkedArticleCount,
          additions,
          hiddenCount,
          uncategorizedCount,
          duplicateLinkCount,
          source,
        };
      }

      async fetchAllArticles() {
        const firstPage = await this.fetchArticlePage(1);
        const articles = [...(firstPage.articles || [])];
        const totalPages = Math.max(1, Number(firstPage.pages) || 1);

        for (let start = 2; start <= totalPages; start += 4) {
          const requests = [];
          for (let page = start; page < start + 4 && page <= totalPages; page += 1) {
            requests.push(this.fetchArticlePage(page));
          }
          const pages = await Promise.all(requests);
          pages.forEach((result) => articles.push(...(result.articles || [])));
        }

        const seen = new Set();
        return articles.filter((article) => {
          const key = String(article.id || this.normalizeUrl(article.url));
          if (!key || seen.has(key)) return false;
          seen.add(key);
          return true;
        });
      }

      async fetchArticlePage(page) {
        const url = new URL(this.dataset.blogUrl, window.location.origin);
        url.searchParams.set("view", "directory-index");
        url.searchParams.set("page", String(page));
        const response = await fetch(url.toString(), {
          credentials: "same-origin",
          headers: { Accept: "application/json, text/plain, */*" },
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return JSON.parse(await response.text());
      }

      appendMissingAutomaticArticles(articles) {
        const list = this.querySelector("[data-tutorial-directory-list]");
        if (!list) return;
        const hiddenUrls = new Set(
          articles
            .filter((article) => this.isArticleHidden(article))
            .map((article) => this.normalizeUrl(article.url)),
        );
        list.querySelectorAll("[data-tutorial-directory-item]").forEach((item) => {
          const link = item.querySelector(".tutorial-directory__link");
          if (link && hiddenUrls.has(this.normalizeUrl(link.getAttribute("href")))) {
            item.remove();
          }
        });
        // 复用统一的“未链接且未隐藏”汇总，保证剩余文章只补充一次。
        this.collectAdditions(articles).forEach(({ article }) => {
          list.appendChild(this.createItem(article));
        });
      }

      appendUnlinkedArticles(articles) {
        const additions = this.collectAdditions(articles);
        const sorted = [...additions].sort((a, b) => {
          const aOrder = this.getArticleOrder(a.article);
          const bOrder = this.getArticleOrder(b.article);
          return (
            a.category.localeCompare(b.category) ||
            a.subcategory.localeCompare(b.subcategory) ||
            aOrder - bOrder ||
            String(a.article.title || "").localeCompare(
              String(b.article.title || ""),
              undefined,
              { sensitivity: "base", numeric: true },
            )
          );
        });

        sorted.forEach(({ article, category, subcategory }) => {
          const list = this.resolveManualList(category, subcategory);
          list?.appendChild(this.createItem(article, category, subcategory));
        });
      }

      // 汇总“未被现有目录链接、且未隐藏”的文章，供目录补充与维护统计复用。
      collectAdditions(articles) {
        const linked = this.getLinkedUrls();
        const additions = [];

        articles.forEach((article) => {
          const url = this.normalizeUrl(article.url);
          if (!url || linked.has(url)) return;
          if (this.isArticleHidden(article)) return;

          const category = this.getArticleCategory(article);
          const subcategory = category ? this.getArticleSubcategory(article) : "";
          additions.push({ article, category, subcategory });
        });

        return additions;
      }

      getLinkedUrls() {
        return new Set(this.getLinkedUrlList());
      }

      getLinkedUrlList() {
        return this.getLinkedEntries().map((entry) => entry.url);
      }

      getLinkedEntries() {
        return Array.from(
          this.querySelectorAll(
            "[data-tutorial-directory-item] .tutorial-directory__link",
          ),
        )
          .map((link) => ({
            url: this.normalizeUrl(link.getAttribute("href")),
            href: link.href,
            title: link.querySelector("span:last-child")?.textContent.trim() ||
              link.textContent.trim(),
          }))
          .filter((entry) => entry.url);
      }

      renderMaintenance(diagnostics) {
        if (!this.maintenance) return;

        const {
          totalArticles,
          configuredItemCount,
          linkedArticleCount,
          additions,
          hiddenCount,
          uncategorizedCount,
          duplicateLinkCount,
          configuredEntries,
          source,
        } = diagnostics;

        this.maintenance.textContent = (source === "manual"
          ? [
              `全部文章 ${totalArticles} 篇`,
              `现有手工条目 ${configuredItemCount} 条`,
              `已手工链接文章 ${linkedArticleCount} 篇`,
              `自动补充 ${additions.length} 篇`,
              `待分类 ${uncategorizedCount} 篇`,
              `标记隐藏 ${hiddenCount} 篇`,
              `重复手工链接 ${duplicateLinkCount} 条`,
            ]
          : [
              `全部文章 ${totalArticles} 篇`,
              `自动收录 ${totalArticles - hiddenCount} 篇`,
              `待分类 ${uncategorizedCount} 篇`,
              `已隐藏 ${hiddenCount} 篇`,
            ]).join("；") + "。";

        if (this.unlinkedLabel) {
          this.unlinkedLabel.textContent =
            source === "manual" ? "未手工配置文章" : "待分类文章";
        }

        // 待分类清单：直接跳转后台对应文章编辑页
        if (this.unlinkedList) {
          this.unlinkedList.replaceChildren();
          additions.forEach(({ article, category, subcategory }) => {
            const label = category
              ? [category, subcategory].filter(Boolean).join(" › ")
              : "待分类";
            this.unlinkedList.appendChild(
              this.createMaintenanceItem(article, label),
            );
          });
        }
        if (this.unlinkedCount) {
          this.unlinkedCount.textContent = String(additions.length);
        }
        if (this.unlinkedDetails) {
          this.unlinkedDetails.hidden = additions.length === 0;
        }

        // 重复链接清单
        const groupedEntries = new Map();
        configuredEntries.forEach((entry) => {
          const group = groupedEntries.get(entry.url) || [];
          group.push(entry);
          groupedEntries.set(entry.url, group);
        });
        const duplicates = Array.from(groupedEntries.values()).filter(
          (entries) => entries.length > 1,
        );

        if (this.duplicateList) {
          this.duplicateList.replaceChildren();
          duplicates.forEach((entries) => {
            const entry = entries[0];
            this.duplicateList.appendChild(
              this.createMaintenanceItem(
                { title: entry.title, url: entry.href },
                `重复 ${entries.length} 次`,
              ),
            );
          });
        }
        const duplicateCount = duplicates.reduce(
          (total, entries) => total + entries.length - 1,
          0,
        );
        if (this.duplicateCount) {
          this.duplicateCount.textContent = String(duplicateCount);
        }
        if (this.duplicateDetails) {
          this.duplicateDetails.hidden = duplicateCount === 0;
        }
      }

      // 维护清单项：有文章 id 时跳转后台编辑页，否则跳转文章前台页。
      createMaintenanceItem(article, label) {
        const item = document.createElement("li");
        const link = document.createElement("a");
        const adminUrl = this.buildAdminArticleUrl(article);
        link.href = adminUrl || article.url || "#";
        link.target = "_blank";
        link.rel = "noopener";
        link.textContent = article.title || "Untitled article";
        const status = document.createElement("small");
        status.textContent = label;
        item.append(link, status);
        return item;
      }

      buildAdminArticleUrl(article) {
        const id = Number(article.id);
        if (!Number.isFinite(id) || id <= 0) return "";
        const shopName = String(this.dataset.shopDomain || "").replace(
          ".myshopify.com",
          "",
        );
        if (!shopName) return "";
        return `https://admin.shopify.com/store/${shopName}/articles/${id}`;
      }

      normalizeUrl(value) {
        if (!value) return "";
        try {
          const url = new URL(value, window.location.origin);
          return url.pathname.replace(/\/$/, "").toLocaleLowerCase();
        } catch (error) {
          return String(value).split(/[?#]/)[0].replace(/\/$/, "").toLocaleLowerCase();
        }
      }

      getArticleCategory(article) {
        return (
          String(article.directory_category || "").trim() ||
          this.getTagValue(article.tags, ["目录一级", "directory-group"])
        );
      }

      getArticleSubcategory(article) {
        return (
          String(article.directory_subcategory || "").trim() ||
          this.getTagValue(article.tags, ["目录二级", "directory-subgroup"])
        );
      }

      getArticleOrder(article) {
        if (article.directory_order === null || article.directory_order === undefined ||
            String(article.directory_order).trim() === "") {
          return Number.MAX_SAFE_INTEGER;
        }
        const value = Number(article.directory_order);
        return Number.isFinite(value) ? value : Number.MAX_SAFE_INTEGER;
      }

      getTagValue(tags, prefixes) {
        const values = Array.isArray(tags) ? tags : [];
        for (const tag of values) {
          const match = String(tag).match(/^\s*([^:：]+)\s*[:：]\s*(.+?)\s*$/);
          if (
            match &&
            prefixes.some(
              (prefix) => this.normalize(match[1]) === this.normalize(prefix),
            )
          ) {
            return match[2].trim();
          }
        }
        return "";
      }

      isArticleHidden(article) {
        if (
          article.directory_hidden === true ||
          String(article.directory_hidden).toLocaleLowerCase() === "true"
        ) {
          return true;
        }
        return (Array.isArray(article.tags) ? article.tags : []).some((tag) => {
          const value = this.normalize(tag);
          return value === "目录隐藏" || value === "directory-hidden";
        });
      }

      resolveManualList(category, subcategory) {
        if (!category) return this.ensureUncategorizedGroup();

        const primary = this.ensurePrimaryGroup(category);
        if (!subcategory) return this.ensurePrimaryDirectList(primary);
        return this.ensureSubgroup(primary, subcategory);
      }

      ensureUncategorizedGroup() {
        let group = this.querySelector(
          '[data-tutorial-directory-group][data-directory-group-kind="uncategorized"]',
        );
        if (!group) {
          const title = this.dataset.uncategorizedTitle || "Other tutorials";
          group = this.createGroup(title, "uncategorized");
          this.getGroupsContainer().prepend(group);
        }
        return group.querySelector(":scope > .tutorial-directory__list");
      }

      ensurePrimaryGroup(title) {
        const normalized = this.normalize(title);
        let group = Array.from(
          this.querySelectorAll(
            '[data-tutorial-directory-group][data-tutorial-directory-level="1"]',
          ),
        ).find(
          (candidate) =>
            this.normalize(candidate.dataset.directoryGroupTitle) === normalized,
        );
        if (!group) {
          group = this.createGroup(title, "primary");
          this.getGroupsContainer().appendChild(group);
        }
        return group;
      }

      ensurePrimaryDirectList(group) {
        let body = group.querySelector(":scope > .tutorial-directory__group-body");
        if (!body) {
          body = document.createElement("div");
          body.className = "tutorial-directory__group-body";
          group.appendChild(body);
        }
        let list = body.querySelector(":scope > .tutorial-directory__list--direct");
        if (!list) {
          list = this.createList("tutorial-directory__list--direct");
          body.prepend(list);
        }
        return list;
      }

      ensureSubgroup(primary, title) {
        const normalized = this.normalize(title);
        let body = primary.querySelector(":scope > .tutorial-directory__group-body");
        if (!body) {
          body = document.createElement("div");
          body.className = "tutorial-directory__group-body";
          primary.appendChild(body);
        }
        let subgroups = body.querySelector(":scope > .tutorial-directory__subgroups");
        if (!subgroups) {
          subgroups = document.createElement("div");
          subgroups.className = "tutorial-directory__subgroups";
          body.appendChild(subgroups);
        }
        let group = Array.from(
          subgroups.querySelectorAll(
            ':scope > [data-tutorial-directory-group][data-tutorial-directory-level="2"]',
          ),
        ).find(
          (candidate) =>
            this.normalize(candidate.dataset.directoryGroupTitle) === normalized,
        );
        if (!group) {
          group = this.createGroup(title, "secondary");
          subgroups.appendChild(group);
        }
        return group.querySelector(":scope > .tutorial-directory__list");
      }

      getGroupsContainer() {
        let container = this.querySelector("[data-tutorial-directory-groups]");
        if (!container) {
          container = document.createElement("div");
          container.className = "tutorial-directory__groups";
          container.dataset.tutorialDirectoryGroups = "";
          this.querySelector(".tutorial-directory__nav")?.appendChild(container);
        }
        return container;
      }

      createGroup(title, type) {
        const group = document.createElement("details");
        group.className = `tutorial-directory__group${
          type === "primary"
            ? " tutorial-directory__group--primary"
            : type === "secondary"
              ? " tutorial-directory__group--secondary"
              : ""
        }`;
        group.dataset.tutorialDirectoryGroup = "";
        group.dataset.directoryGroupTitle = title;
        if (type === "uncategorized") {
          group.dataset.directoryGroupKind = "uncategorized";
        } else {
          group.dataset.tutorialDirectoryLevel = type === "primary" ? "1" : "2";
        }
        group.open = this.dataset.groupsOpen === "true";

        const summary = document.createElement("summary");
        summary.className = "tutorial-directory__group-summary";
        const titleElement = document.createElement("span");
        titleElement.className = "tutorial-directory__group-title";
        titleElement.textContent = title;
        const count = document.createElement("span");
        count.className = "tutorial-directory__group-count";
        count.dataset.tutorialDirectoryGroupCount = "";
        count.dataset.total = "0";
        count.textContent = "0";
        const caret = this.querySelector(
          ".tutorial-directory__group-caret",
        )?.cloneNode(true);
        summary.append(titleElement, count);
        if (caret) summary.appendChild(caret);
        group.appendChild(summary);

        if (type === "primary") {
          const body = document.createElement("div");
          body.className = "tutorial-directory__group-body";
          const subgroups = document.createElement("div");
          subgroups.className = "tutorial-directory__subgroups";
          body.appendChild(subgroups);
          group.appendChild(body);
        } else {
          group.appendChild(this.createList());
        }
        return group;
      }

      createList(extraClass = "") {
        const list = document.createElement("ol");
        list.className = `tutorial-directory__list list-unstyled ${extraClass}`.trim();
        return list;
      }

      createItem(article, category = "", subcategory = "") {
        const item = document.createElement("li");
        item.className = "tutorial-directory__item";
        item.dataset.tutorialDirectoryItem = "";
        item.dataset.title = [article.title, category, subcategory]
          .filter(Boolean)
          .join(" ")
          .toLocaleLowerCase();
        item.dataset.directoryAutomatic = "true";

        const link = document.createElement("a");
        link.className = "tutorial-directory__link focus-inset";
        link.href = article.url;
        const number = document.createElement("span");
        number.className = "tutorial-directory__number";
        number.setAttribute("aria-hidden", "true");
        const title = document.createElement("span");
        title.textContent = article.title || "Untitled article";
        link.append(number, title);
        item.appendChild(link);
        return item;
      }

      normalize(value) {
        return String(value)
          .normalize("NFKD")
          .replace(/[\u0300-\u036f]/g, "")
          .toLocaleLowerCase()
          .trim();
      }

      syncDisclosure(viewport) {
        if (!this.details) return;

        const isMobile = viewport.matches;
        if (isMobile && this.lastViewportWasMobile !== true) {
          this.details.open = false;
        } else if (!isMobile) {
          this.details.open = true;
        }
        this.lastViewportWasMobile = isMobile;
      }

      filter() {
        const term = this.normalize(this.input.value);
        const tokens = term.split(/\s+/).filter(Boolean);
        let visible = 0;

        this.items.forEach((item) => {
          const matches = tokens.every((token) =>
            item._normalizedTitle.includes(token),
          );
          item.hidden = !matches;
          if (matches) visible += 1;
        });

        this.groups.forEach((group) => {
          const groupVisible = group._directoryItems.filter(
            (item) => !item.hidden,
          ).length;
          const groupCount = group.querySelector(
            "[data-tutorial-directory-group-count]",
          );

          if (groupCount) {
            groupCount.textContent = String(
              tokens.length
                ? groupVisible
                : groupCount.dataset.total || group._directoryItems.length,
            );
          }

          if (tokens.length) {
            if (group._openBeforeSearch === undefined) {
              group._openBeforeSearch = group.open;
            }
            group.hidden = groupVisible === 0;
            group.open = groupVisible > 0;
          } else {
            group.hidden = false;
            if (typeof group._openBeforeSearch === "boolean") {
              group.open = group._openBeforeSearch;
              group._openBeforeSearch = undefined;
            }
          }
        });

        if (this.count) this.count.textContent = String(visible);
        if (this.empty) this.empty.classList.toggle("hidden", visible !== 0);
        if (this.clearButton) {
          this.clearButton.classList.toggle("hidden", term.length === 0);
        }
      }

      clear() {
        this.input.value = "";
        this.filter();
        this.input.focus();
      }
    },
  );
}
